import { useEffect, useState } from "react";
import { Gamepad2, Loader2, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { SpeakButton } from "./SpeakButton";
import { words } from "../lib/api";
import { forgetSaved, markSaved } from "./WordBalloon";
import { WordQuiz } from "./WordQuiz";

const WordCard = ({ w, onRemove }) => {
  const slug = w.word.replace(/[^a-z0-9]+/g, "-");
  return (
    <li data-testid={`my-word-${slug}`} className="what-word-card">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-lg font-bold text-white capitalize">{w.word}</p>
        <span className="flex items-center gap-3">
          <SpeakButton big load={() => words.listen(w.word)} testid={`my-word-listen-${slug}`} />
          <button data-testid={`my-word-remove-${slug}`} onClick={() => onRemove(w.word)} aria-label={`Remove ${w.word}`} className="text-slate-500 hover:text-red-400">
            <Trash2 size={15} />
          </button>
        </span>
      </div>
      <p className="text-[15px] text-slate-200 mt-2">{w.meaning}</p>
      {w.alt_meaning && <p className="text-sm text-slate-400 mt-1">{w.alt_meaning}</p>}
      {w.example && <p className="text-sm italic text-[#7DD3FC] mt-2">"{w.example}"</p>}
    </li>
  );
};

export const MyWordsDialog = ({ open, onOpenChange }) => {
  const [list, setList] = useState(null);
  const [quiz, setQuiz] = useState(false);
  useEffect(() => {
    setQuiz(false);
    if (open) words.list().then((l) => { markSaved(l.map((w) => w.word)); setList(l); }).catch(() => { setList([]); toast.error("Couldn't load your words"); });
  }, [open]);

  const remove = async (word) => {
    try {
      await words.remove(word);
      forgetSaved(word);
      setList((l) => l.filter((w) => w.word !== word));
    } catch {
      toast.error("Couldn't remove that word");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="my-words-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#0E131D] border-white/[0.08] text-slate-200 p-8 font-['Plus_Jakarta_Sans',sans-serif]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Star size={20} className="text-[#FBBF24]" /> My Words</DialogTitle>
          <DialogDescription>Words you saved. Tap Listen to hear them again.</DialogDescription>
        </DialogHeader>
        {!list ? (
          <Loader2 className="animate-spin text-slate-500 mx-auto my-8" />
        ) : quiz ? (
          <WordQuiz list={list} onBack={() => setQuiz(false)} />
        ) : list.length === 0 ? (
          <p data-testid="my-words-empty" className="text-sm text-slate-400 py-6">No words yet! Tap an underlined word in an answer, then press "Save to My Words".</p>
        ) : (
          <>
            <button data-testid="play-quiz-btn" onClick={() => setQuiz(true)} className="what-quiz-play">
              <Gamepad2 size={18} /> Play the word quiz
            </button>
            <ul data-testid="my-words-list" className="space-y-3">{list.map((w) => <WordCard key={w.word} w={w} onRemove={remove} />)}</ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
